export type ServiceLineId = 'intelligence' | 'growth' | 'ai'

export interface PackageOption {
  name: string
  summary: string
  includes: string[]
}

export interface UseCase {
  sector: string
  outcome: string
}

export interface ServiceLine {
  id: ServiceLineId
  name: string
  short: string
  promise: string
  who: string
  outcome: string
  subServices: { name: string; detail: string }[]
  useCases: UseCase[]
  packages: PackageOption[]
}

export const SECTORS = [
  'NGOs',
  'Government',
  'Banking',
  'Health',
  'Agriculture',
  'SMEs',
  'Education',
  'Energy',
  'Logistics',
  'Telecoms',
] as const

export type Sector = (typeof SECTORS)[number]

export const ENGAGEMENT_MODELS = [
  { id: 'project', name: 'Project', detail: 'A fixed scope with a defined start and finish.' },
  { id: 'retainer', name: 'Retainer', detail: 'A standing monthly commitment across three months or more.' },
  { id: 'advisory', name: 'Advisory', detail: 'Scheduled counsel for a leadership team.' },
] as const

export type EngagementModelId = (typeof ENGAGEMENT_MODELS)[number]['id']

export const SERVICE_LINES: ServiceLine[] = [
  {
    id: 'intelligence',
    name: 'Cavalla Intelligence',
    short: 'Market intelligence',
    promise: 'Know the market before you commit capital to it.',
    who: 'Boards, strategy teams and investment committees deciding where to enter, expand or withdraw.',
    outcome:
      'A defensible view of the market, sized and sourced, with the risks named and the next decision made clear.',
    subServices: [
      { name: 'Market sizing and entry', detail: 'Bottom up sizing, segment maps and a costed entry route for the markets you are weighing.' },
      { name: 'Competitor and pricing analysis', detail: 'Who is winning, on what terms, and where your pricing sits against them.' },
      { name: 'Demand and opportunity mapping', detail: 'Where demand sits today and where the value moves by 2028, read sector by sector.' },
      { name: 'Sector and policy briefings', detail: 'Regular briefings on the regulation, funding and buyer behaviour that move your sector.' },
    ],
    useCases: [
      { sector: 'Banking', outcome: 'Sized the SME lending opportunity across three West African markets and named the two worth entering first.' },
      { sector: 'Agriculture', outcome: 'Mapped offtaker demand for a processing group and repriced two product lines against imports.' },
      { sector: 'Government', outcome: 'Briefed a ministry on private investment appetite ahead of a concession round.' },
    ],
    packages: [
      { name: 'Market Scan', summary: 'A focused read on one market and one segment.', includes: ['Bottom up market sizing', 'Competitor and pricing view', 'Written brief and a working session'] },
      { name: 'Market Entry Study', summary: 'A full entry case for a board decision.', includes: ['Three market comparison', 'Demand and channel mapping', 'Costed entry route', 'Board ready pack'] },
      { name: 'Intelligence Retainer', summary: 'A standing view of your sector, refreshed monthly.', includes: ['Monthly sector briefing', 'Quarterly deep dive', 'Analyst access on request'] },
    ],
  },
  {
    id: 'growth',
    name: 'Cavalla Growth',
    short: 'Data led growth',
    promise: 'Turn a proven market into a working route to revenue.',
    who: 'Commercial leaders and founders who know the opportunity is real and need demand to arrive predictably.',
    outcome:
      'A go-to-market plan your team can run on Monday, with the segments chosen, the offer priced and the pipeline instrumented.',
    subServices: [
      { name: 'Go-to-market strategy', detail: 'Segments, positioning, offer and channel, set out as a plan with owners and dates.' },
      { name: 'Pricing and packaging', detail: 'Offer architecture and price points tested against willingness to pay.' },
      { name: 'Demand generation systems', detail: 'Content, campaigns and CRM built into one measurable pipeline.' },
      { name: 'Revenue analytics', detail: 'The dashboard that tells your board what is working, weekly.' },
    ],
    useCases: [
      { sector: 'SMEs', outcome: 'Rebuilt the offer and pricing for a services firm and lifted average deal value by a third.' },
      { sector: 'Health', outcome: 'Built a referral pipeline and reporting for a diagnostics group across two cities.' },
      { sector: 'NGOs', outcome: 'Turned a scattered donor outreach effort into a tracked, sequenced programme.' },
    ],
    packages: [
      { name: 'Go-to-Market Sprint', summary: 'Four weeks to a written, costed route to market.', includes: ['Segment and offer definition', 'Pricing and packaging', 'Channel plan', 'Ninety day rollout schedule'] },
      { name: 'Demand Engine Build', summary: 'The system that makes demand repeatable.', includes: ['CRM and pipeline build', 'Campaign and content engine', 'Reporting dashboard', 'Team enablement'] },
      { name: 'Growth Retainer', summary: 'We run the engine alongside your team.', includes: ['Monthly planning and review', 'Campaign execution support', 'Pipeline and revenue reporting'] },
    ],
  },
  {
    id: 'ai',
    name: 'Cavalla AI',
    short: 'Augmented intelligence',
    promise: 'Put AI to work where it changes the numbers, and nowhere else.',
    who: 'Executives under pressure to adopt AI who need a grounded, governed place to start.',
    outcome:
      'Working AI in the hands of your people, with the data, the guardrails and the training that keep it useful.',
    subServices: [
      { name: 'AI readiness and strategy', detail: 'An honest assessment of data, skills, use cases, governance and budget, with a sequenced plan.' },
      { name: 'Use case design and build', detail: 'We select the two or three use cases worth building, then build them.' },
      { name: 'Data foundations', detail: 'The pipelines, definitions and quality checks that make any of this dependable.' },
      { name: 'Governance and enablement', detail: 'Policy, review and training so adoption holds after we leave.' },
    ],
    useCases: [
      { sector: 'Government', outcome: 'Designed a document triage assistant for a records unit, with a human review step at every decision.' },
      { sector: 'Banking', outcome: 'Built a credit memo drafting assistant that cut preparation time by half.' },
      { sector: 'Education', outcome: 'Set the AI use policy and trained two hundred staff across a university group.' },
    ],
    packages: [
      { name: 'AI Readiness', summary: 'Where you stand and what to do first.', includes: ['Readiness assessment across five dimensions', 'Scored report and benchmark', 'Prioritised use case shortlist', 'Executive briefing'] },
      { name: 'AI Pilot Build', summary: 'One use case, built, governed and adopted.', includes: ['Use case design', 'Working build and integration', 'Governance and review process', 'Team training and handover'] },
      { name: 'AI Advisory', summary: 'Standing counsel for your leadership team.', includes: ['Monthly advisory session', 'Policy and guardrail review', 'Vendor and build guidance'] },
    ],
  },
]

export function getServiceLine(id: string): ServiceLine | undefined {
  return SERVICE_LINES.find((line) => line.id === id)
}

export const METHOD_STEPS = [
  {
    id: 'capture',
    title: 'Capture',
    summary: 'We gather the evidence that already exists inside and around your organisation.',
    detail:
      'Most organisations are not short of data. They are short of a shared, trusted view of it. We pull together internal records, market sources, policy signals and buyer behaviour, then agree what each number actually means before anyone acts on it.',
    output: 'A single evidence base your team agrees on.',
  },
  {
    id: 'apply',
    title: 'Apply',
    summary: 'We apply analysis and AI where it sharpens the answer, not where it looks impressive.',
    detail:
      'Analysis is chosen to fit the question. Some of it is machine assisted, some of it is careful human work. We are explicit about which is which, and about the confidence you should place in each finding.',
    output: 'Findings you can defend in front of a board.',
  },
  {
    id: 'enable',
    title: 'Enable',
    summary: 'We build the capability so the work continues without us.',
    detail:
      'We hand over the models, the dashboards, the definitions and the training. Your people learn the method, not just the result, which is what keeps the value compounding after the engagement closes.',
    output: 'A team that can repeat the work.',
  },
  {
    id: 'decide',
    title: 'Decide',
    summary: 'We stay in the room for the decision, and for what follows it.',
    detail:
      'A report that does not change a decision has failed. We frame the options, name the trade offs and the risks, and remain available while the decision is implemented and tested against reality.',
    output: 'A decision made, with the reasoning recorded.',
  },
] as const

export const DEMAND_MAP: {
  sector: string
  now: { label: string; intensity: number; note: string }
  future: { label: string; intensity: number; note: string }
}[] = [
  {
    sector: 'Banking',
    now: { label: 'Fraud and risk analytics', intensity: 82, note: 'Spending is concentrated in detection and regulatory reporting.' },
    future: { label: 'Advisory grade credit automation', intensity: 91, note: 'Value moves to assisted underwriting and portfolio decisioning.' },
  },
  {
    sector: 'Health',
    now: { label: 'Records digitisation', intensity: 74, note: 'Budget sits with getting paper into systems that talk to each other.' },
    future: { label: 'Population level demand forecasting', intensity: 88, note: 'Planning capacity and supply ahead of need becomes the buying reason.' },
  },
  {
    sector: 'Agriculture',
    now: { label: 'Yield and input monitoring', intensity: 68, note: 'Remote sensing and input tracking dominate current spend.' },
    future: { label: 'Offtake and price intelligence', intensity: 84, note: 'Margin shifts to whoever reads demand and price movement first.' },
  },
  {
    sector: 'Government',
    now: { label: 'Service digitisation', intensity: 71, note: 'Front line service delivery and citizen access lead the agenda.' },
    future: { label: 'Evidence led policy modelling', intensity: 86, note: 'Simulation and scenario work move into the centre of policy units.' },
  },
  {
    sector: 'NGOs',
    now: { label: 'Donor reporting', intensity: 65, note: 'Compliance reporting absorbs most of the analytical effort.' },
    future: { label: 'Programme impact measurement', intensity: 81, note: 'Funders increasingly price grants against measured outcomes.' },
  },
  {
    sector: 'SMEs',
    now: { label: 'Basic automation', intensity: 58, note: 'Invoicing, scheduling and customer records come first.' },
    future: { label: 'Assisted sales and service', intensity: 79, note: 'Small teams compete by putting AI in front of customers.' },
  },
]
